/**
 * `useProjectMembers` — live read of project-linked app users.
 *
 * Mirrors the mobile hook at
 *   /Users/gudupuramesh/Documents/siteexpens/src/features/projects/useProjectMembers.ts
 *
 * Reads `projects/{projectId}.memberIds[]` (staff) PLUS
 * `projects/{projectId}.clientUids[]` (project-scoped clients), then
 * resolves each uid against `organizations/{orgId}/memberPublic/{uid}`
 * — the rules-safe projection that holds displayName / photoURL /
 * phoneNumber / roleKey. Missing memberPublic docs (race window
 * after invite, before the projection trigger runs) fall back to
 * "Member" placeholders so the UI never breaks.
 *
 * We deliberately do NOT read peer `users/{uid}` docs from the
 * client — Firestore rules block cross-user reads. memberPublic is
 * the only sanctioned read path.
 */
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  documentId,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { db } from '@/lib/firebase';

// RoleKey moved to `@shared/perms/roles` — define once, both mobile
// and web import from there. Re-exported here so the many existing
// `import { RoleKey } from '@/lib/useProjectMembers'` call sites
// in this codebase keep working.
import type { RoleKey } from '@shared/perms';
export type { RoleKey };

export type ProjectMember = {
  uid: string;
  displayName: string;
  photoURL: string | null;
  phoneNumber: string | null;
  /** Role of this member in the org. Sourced from
   *  `memberPublic.roleKey`. Null in the race window after invite
   *  but before the projection trigger writes the doc — render a
   *  fallback. */
  role: RoleKey | null;
  /** True when uid is on this project via `clientUids` only (not
   *  `memberIds`). */
  isProjectClient: boolean;
};

const VALID_ROLES = new Set<RoleKey>([
  'superAdmin',
  'admin',
  'manager',
  'accountant',
  'siteEngineer',
  'supervisor',
  'designer',
  'viewer',
  'client',
]);

export type UseProjectMembersResult = {
  members: ProjectMember[];
  loading: boolean;
};

/** Firestore `where(documentId(), 'in', ...)` supports max 30 ids
 *  per call (v9 SDK lifted from 10). Chunk to stay safe. */
async function fetchMemberPublic(
  orgId: string,
  uids: string[],
): Promise<Map<string, Record<string, unknown>>> {
  const out = new Map<string, Record<string, unknown>>();
  if (uids.length === 0) return out;
  const col = collection(db, 'organizations', orgId, 'memberPublic');
  for (let i = 0; i < uids.length; i += 30) {
    const chunk = uids.slice(i, i + 30);
    const snap = await getDocs(query(col, where(documentId(), 'in', chunk)));
    snap.forEach((d) => out.set(d.id, d.data() as Record<string, unknown>));
  }
  return out;
}

export function useProjectMembers(
  projectId: string | undefined,
): UseProjectMembersResult {
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) {
      setMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      doc(db, 'projects', projectId),
      async (snap) => {
        if (cancelled) return;
        const data = snap.data() as
          | { memberIds?: string[]; clientUids?: string[]; orgId?: string }
          | undefined;
        const memberIds = data?.memberIds ?? [];
        const clientUids = data?.clientUids ?? [];
        const orgId = typeof data?.orgId === 'string' ? data.orgId : '';
        const staffSet = new Set(memberIds);
        const uidSet = new Set<string>([...memberIds, ...clientUids]);
        const uids = [...uidSet];

        if (uids.length === 0) {
          setMembers([]);
          setLoading(false);
          return;
        }

        try {
          let resolved: ProjectMember[];
          if (!orgId) {
            resolved = uids.map((uid) => ({
              uid,
              displayName: 'Member',
              photoURL: null,
              phoneNumber: null,
              role: null,
              isProjectClient: clientUids.includes(uid) && !staffSet.has(uid),
            }));
          } else {
            const lookup = await fetchMemberPublic(orgId, uids);
            if (cancelled) return;
            resolved = uids.map((uid) => {
              const u = lookup.get(uid);
              const inStaff = staffSet.has(uid);
              const inClientList = clientUids.includes(uid);
              const role: RoleKey | null =
                typeof u?.roleKey === 'string' &&
                VALID_ROLES.has(u.roleKey as RoleKey)
                  ? (u.roleKey as RoleKey)
                  : null;
              return {
                uid,
                displayName:
                  typeof u?.displayName === 'string' &&
                  (u.displayName as string).trim()
                    ? (u.displayName as string)
                    : 'Member',
                photoURL: (u?.photoURL as string | null | undefined) ?? null,
                phoneNumber:
                  typeof u?.phoneNumber === 'string'
                    ? (u.phoneNumber as string)
                    : null,
                role,
                isProjectClient: inClientList && !inStaff,
              };
            });
          }
          resolved.sort((a, b) =>
            a.displayName.localeCompare(b.displayName, undefined, {
              sensitivity: 'base',
            }),
          );
          setMembers(resolved);
          setLoading(false);
        } catch (err) {
          console.warn('[useProjectMembers] memberPublic fetch error:', err);
          setLoading(false);
        }
      },
      (err) => {
        console.warn('[useProjectMembers] project snapshot error:', err);
        setLoading(false);
      },
    );
  }, [projectId]);

  return { members, loading };
}

// ───── Role labels + assignable list ─────
// Both moved to `@shared/perms` so mobile + web read identical
// constants. Re-exported here so existing
// `import { ROLE_LABELS } from '@/lib/useProjectMembers'` etc.
// call sites keep working.

export { ROLE_LABELS, ASSIGNABLE_ROLES_BY_SUPER_ADMIN as ASSIGNABLE_ROLES } from '@shared/perms';
export type { AssignableRole } from '@shared/perms';
